import { useState, useEffect, useCallback } from "react";
import TypeWriterText from "../../components/TypeWriterText";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import KennelSEO, { breadcrumbSchema } from "./KennelSEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";

const KennelVideos = () => {
  useOutletContext();
  const [activeVideo, setActiveVideo] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    fetch(`${API}/videos`).then(r => r.json()).then(data => setVideos(data)).catch(() => {});
  }, []);

  const handleClose = useCallback(() => setActiveVideo(null), []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleClose]);

  return (
    <div>
      <KennelSEO
        title="Filmy | Wolfe vom Katzenberg — Hodowla Owczarka Niemieckiego"
        description="Filmy z hodowli owczarków niemieckich Wolfe vom Katzenberg."
        canonical="https://wolfevomkatzenberg.pl/galeria/filmy"
        schema={[breadcrumbSchema([{ name: "Start", href: "/" }, { name: "Galeria", href: "/galeria" }, { name: "Filmy", href: "/galeria/filmy" }])]}
      />
      <section className="bg-[#141416] border-b border-[#2A2A2D] px-6 md:px-12 pt-16 pb-24 relative overflow-hidden min-h-[220px]">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-[#D4AF37]/[0.03] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 40px)" }} />
        <div className="absolute inset-0 flex items-center justify-end pointer-events-none overflow-hidden">
          <motion.img src="/kennel_motif.png" alt="" className="w-[500px] md:w-[700px] lg:w-[900px] max-w-none h-auto object-contain mr-[-5%]"
            initial={{ opacity: 0 }} animate={{ opacity: [0, 0.12, 0.12, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", times: [0, 0.2, 0.8, 1] }}
          />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">GALERIA</span>
            <TypeWriterText />
            <h1 className="font-serif text-[clamp(2rem,4vw,3.5rem)] text-[#F5F5F5] mb-4">Filmy</h1>
            <div className="w-16 h-px bg-[#D4AF37]/30" />
          </motion.div>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-12 py-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
          {videos.map((vid, idx) => (
            <motion.div key={vid.src} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + idx * 0.1, duration: 0.4 }}
              className="relative bg-[#1A1A1D] cursor-pointer group overflow-hidden"
              onClick={() => setActiveVideo(idx)} data-testid={`video-item-${idx}`}
            >
              <video src={vid.src} muted loop playsInline preload="metadata"
                className="w-full aspect-video object-cover"
                onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} />
              <div className="absolute inset-0 bg-[#0B0B0D]/30 group-hover:bg-[#0B0B0D]/10 transition-colors duration-300" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 rounded-full bg-[#0B0B0D]/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#D4AF37]/80 transition-colors duration-300">
                  <Play className="w-7 h-7 text-[#F5F5F5] ml-1" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0B0B0D]/80 to-transparent">
                <p className="text-[#F5F5F5] text-xs tracking-wider">{vid.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeVideo !== null && videos[activeVideo] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-[#0B0B0D]/95 backdrop-blur-sm flex items-center justify-center" onClick={handleClose} data-testid="video-lightbox"
          >
            <button onClick={handleClose} className="absolute top-6 right-6 z-50 w-12 h-12 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#F5F5F5]" data-testid="video-lightbox-close"><X className="w-5 h-5" /></button>
            <motion.video key={videos[activeVideo].src} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              src={videos[activeVideo].src} autoPlay loop playsInline controls
              className="max-w-[90vw] max-h-[85vh] object-contain" onClick={e => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default KennelVideos;
